import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../api';
import DynamicBanner from '../../Pages/include/DynamicBanner';
import './GeneralDepartment.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import NotFound from '../../Pages/NotFound/NotFound';

const DynamicGenDepartments = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('');
  const [activeInnerTab, setActiveInnerTab] = useState('');
  // Mobile states
const [mobileMainTab, setMobileMainTab] = useState('');
const [mobileInnerTab, setMobileInnerTab] = useState('');


  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api/general-hospital/departments/${slug}`);
        const fetchedData = response.data;
        setData(fetchedData);

       if (fetchedData.main_tabs && fetchedData.main_tabs.length > 0) {
  const firstMainTab = fetchedData.main_tabs[0].id;
  const firstInnerTab =
    fetchedData.main_tabs[0].inner_tabs?.[0]?.id || firstMainTab;

  // Desktop
  setActiveMainTab(firstMainTab);
  setActiveInnerTab(firstInnerTab);

  // Mobile
  setMobileMainTab(firstMainTab);
  setMobileInnerTab(firstInnerTab);
}

    
      } catch (err) {
        console.error('Error fetching department details:', err);
        setError('Department not found or data error.');
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [slug]);

  if (loading) return null;

  if (notFound) return <NotFound />;

  if (error || !data) return null;

  const { banner_data, main_tabs, tab_content } = data;

  const getMainTabById = (id) => main_tabs.find((t) => t.id === id);
  const activeMainTabObj = getMainTabById(activeMainTab);

  const renderDynamicBlock = (block) => {
    const {
      type,
      heading,
      text_content,
      image_url,
      image_position,
      list_items,
      table_headers,
      table_rows,
      fa_icon,
    } = block;

    if (type === 'heading_block') {
      return (
        <div className="sec-title text-center py-3">
          <h2>{heading}</h2>
        </div>
      );
    }

    if (type === 'paragraph_block') {
      return (
        <p
          style={{ textAlign: 'justify' }}
          dangerouslySetInnerHTML={{ __html: text_content }}
        />
      );
    }

    if (type === 'image_only') {
      return (
        <div className="row justify-content-center mb-4">
          <div className="col-md-6 col-12 text-center">
            <img
              src={`${process.env.REACT_APP_API_URL}/public${image_url}`}
              alt={heading || 'Department Image'}
              className="img-fluid rounded"
            />
          </div>
        </div>
      );
    }

    if (type === 'anchor_tag') {
      return (
        <p>
          <strong>Click Here for :</strong>
          <a
            href={image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center gap-2 ms-2"
          >
            <i className="fa fa-hand-o-right text-danger" />
            {text_content}
          </a>
        </p>
      );
    }

    if (type === 'section_image' || type === 'normal_section') {
      const hasImage = type === 'section_image' && image_url;
      const imgCol = hasImage ? 4 : 0;
      const textCol = 12 - imgCol;
      const isLeft = image_position === 'left';

      return (
        <div className="row d-flex align-items-center mb-4">
          <div
            className={`col-md-${textCol} col-12 ${
              hasImage && isLeft ? 'order-2' : 'order-1'
            }`}
          >
            {heading && <h4>{heading}</h4>}
            <p dangerouslySetInnerHTML={{ __html: text_content }} />
          </div>
          {hasImage && (
            <div
              className={`col-md-${imgCol} col-12 text-center ${
                hasImage && isLeft ? 'order-1' : 'order-2'
              }`}
            >
              <img
                src={`${process.env.REACT_APP_API_URL}/public${image_url}`}
                alt={heading}
                className="img-fluid rounded"
                style={{ maxWidth: '100%' }}
              />
            </div>
          )}
        </div>
      );
    }

    if (type === 'list_items') {
      const itemsArray = Array.isArray(list_items)
        ? list_items
        : Object.values(list_items || {});
      const iconClass = fa_icon || 'fa-dot-circle-o';

      return (
        <div>
          {heading && (
            <div className="sec-title text-center py-3">
              <h2>{heading}</h2>
            </div>
          )}
          <ul className="list-unstyled fa-ul dep-list-content opcare-list visitor">
            {itemsArray.map((item, i) => (
              <li key={i}>
                <i className={`fa ${iconClass} me-2`} /> {item}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (type === 'table_block') {
      const headers = Array.isArray(table_headers)
        ? table_headers
        : Object.values(table_headers || {});

      let rows = [];

      if (Array.isArray(table_rows)) {
        rows = table_rows.map(r =>
          Array.isArray(r) ? r : Object.values(r || {})
        );
      } else if (typeof table_rows === "object" && table_rows !== null) {
        rows = Object.values(table_rows).map(r =>
          Array.isArray(r) ? r : Object.values(r || {})
        );
        rows = Object.values(table_rows).map(r =>
          Array.isArray(r) ? r : Object.values(r || {})
        );
      } else {
        rows = [];
      }

      return (
        <div className="table-responsive mb-4">
          {heading && <h4 className="mt-3">{heading}</h4>}
          <table className="table table-bordered table-striped dep-table">
            {headers.length > 0 && (
              <thead>
                <tr>
                  {headers.map((header, hIdx) => (
                    <th key={hIdx}>{header}</th>
                  ))}
                </tr>
              </thead>
            )}

            <tbody>
              {rows.length > 0 ? (
                rows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} dangerouslySetInnerHTML={{ __html: cell }} />
                    ))}
                  </tr>
                ))
              ) : null}
            </tbody>
          </table>
        </div>
      );
    }

    if (type === 'carousel_images') {
      const images = block.carousel_images_data || [];
      const perView = Number(block.carousel_count) || 1;
      const heading = block.heading;

      if (!images.length) return null;

      return (
        <div className="hospital-slider mb-4">
          {heading && <h4 className="my-3">{heading}</h4>}
          <Swiper
            grabCursor={false}
            centeredSlides={false}
            loop={false}
            spaceBetween={20}
            autoplay={{
              delay: 2500,
              disableOnInteraction: true,
            }}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: Math.min(perView, images.length),
              },
            }}
            modules={[Autoplay]}
            className="campusSwiper"
          >
            {images.map((img, index) => (
              <SwiperSlide key={index}>
                <img
                  src={`${process.env.REACT_APP_API_URL}/public${img.url}`}
                  alt={img.alt || `Department image ${index + 1}`}
                  className="swiper-slide-img"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      );
    }

    return <p className="text-warning">Unknown content block type: {type}</p>;
  };

  const renderBlocksForTab = (tabId) => {
    const blocksToRender = tab_content[tabId] || [];

    if (!blocksToRender.length) {
      return <div className="alert alert-info">No content defined for this section.</div>;
    }

    return blocksToRender.map((block, index) => (
      <div key={block.id || index}>{renderDynamicBlock(block)}</div>
    ));
  };

  return (
    <>
      <DynamicBanner
        title={banner_data.title}
        bgImage={banner_data.desktop_images}
        bgImageMobile={banner_data.mobile_images}
      />

      <section className="pt_80 pb_80 general-department-section general-hospital-section">
        <div className="container">
          <div className="row">
            <div className="col-md-3 d-none d-md-block">
              <div className="sidebar sidebar-medicine-medicine-medicine">
                <nav
                  className="nav flex-column nav-pills"
                  id="contentTabs"
                  role="tablist"
                >
                  {main_tabs.map((mainTab) => (
                    <button
                      key={mainTab.id}
                      className={`nav-link ${
                        mainTab.id === activeMainTab ? 'active' : ''
                      } ${mainTab.inner_tabs?.length ? 'has-inner-tabs' : ''}`}
                      onClick={() => {
                        setActiveMainTab(mainTab.id);
                        setActiveInnerTab(
                          mainTab.inner_tabs?.[0]?.id || mainTab.id
                        );
                      }}
                      type="button"
                      role="tab"
                    >
                      {mainTab.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="col-md-9 col-12 d-none d-md-block">
              <div className="specialty-block">
                <div className="tab-content dep-content hospital-content">
                  <div
                    className="tab-pane fade show active"
                    id={`${activeMainTab}-desktop-wrapper`}
                  >
                    <div className="document-box document-box-medicine-medicine">
                      {activeMainTabObj && (
                        <div className="sec-title text-center py-3">
                          <h2>
                            {tab_content[activeMainTab]?.find(
                              (b) => b.type === 'heading_block'
                            )?.heading || activeMainTabObj.title}
                          </h2>
                        </div>
                      )}

                      {activeMainTabObj?.inner_tabs?.length ? (
                        <>
                          <ul
                            className="nav nav-tabs general-tabs d-flex justify-content-center align-items-center mb-3"
                            id="campusTabs"
                            role="tablist"
                          >
                            {activeMainTabObj.inner_tabs.map((innerTab) => (
                              <li
                                className="nav-item"
                                role="presentation"
                                key={innerTab.id}
                              >
                                <button
                                  className={`nav-link ${
                                    innerTab.id === activeInnerTab
                                      ? 'active'
                                      : ''
                                  }`}
                                  type="button"
                                  role="tab"
                                  onClick={() => setActiveInnerTab(innerTab.id)}
                                >
                                  {innerTab.title}
                                </button>
                              </li>
                            ))}
                          </ul>

                          {renderBlocksForTab(activeInnerTab)}
                        </>
                      ) : (
                        renderBlocksForTab(activeMainTab)
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Accordion */}
<div className="col-12 d-md-none">
  <div className="accordion" id="mobileAccordionDepartment">
    {main_tabs.map((mainTab, index) => {
      const isFirstMainTab = index === 0;
      return (
        <div className="accordion-item" key={mainTab.id}>
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${isFirstMainTab ? '' : 'collapsed'}`}
              type="button" onClick={() => {
  setMobileMainTab(mainTab.id);
  setMobileInnerTab(
    mainTab.inner_tabs?.[0]?.id || mainTab.id
  );
}}

              data-bs-toggle="collapse"
              data-bs-target={`#mainTabMobile${mainTab.id}`}
            >
              {mainTab.title}
            </button>
          </h2>
          
          <div
            id={`mainTabMobile${mainTab.id}`}
            className={`accordion-collapse collapse ${isFirstMainTab ? 'show' : ''}`}
            data-bs-parent="#mobileAccordionDepartment"
          >
            <div className="accordion-body">
              {/* If main tab has inner tabs, show nested accordion */}
              {mainTab.inner_tabs && mainTab.inner_tabs.length > 0 ? (
                <div className="accordion" id={`innerAccordion${mainTab.id}`}>
                  {mainTab.inner_tabs.map((innerTab, innerIndex) => {
                    const isFirstInnerTab = innerIndex === 0;
                    return (
                      <div className="accordion-item" key={innerTab.id}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${isFirstInnerTab ? '' : 'collapsed'}`}
                            type="button" onClick={() => setMobileInnerTab(innerTab.id)}

                            data-bs-toggle="collapse"
                            data-bs-target={`#innerTabMobile${innerTab.id}`}
                          >
                            {innerTab.title}
                          </button>
                        </h2>
                        
                        <div
                          id={`innerTabMobile${innerTab.id}`}
                          className={`accordion-collapse collapse ${isFirstInnerTab ? 'show' : ''}`}
                          data-bs-parent={`#innerAccordion${mainTab.id}`}
                        >
                          <div className="accordion-body">
                            
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // If no inner tabs, render main tab content directly
                renderBlocksForTab(mainTab.id)
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
</div>
{/* Mobile Content (OUTSIDE accordion) */}
<div className="col-12 d-md-none mt-3">
  <div className="document-box">
    {renderBlocksForTab(mobileInnerTab || mobileMainTab)}
  </div>
</div>

          </div>
        </div>
      </section>
    </>
  );
};

export default DynamicGenDepartments;
